<template>
  <section class="next-race-card" aria-labelledby="next-race-title">
    <img class="race-image" :src="image" alt="" />
    <div class="card-body">
      <div class="card-label">{{ $t("next_race_title") }}</div>
      <div class="card-sub">{{ $t("next_race_based_on") }}</div>

      <h2 id="next-race-title" class="race-name">{{ event.name }}</h2>

      <ul class="race-meta">
        <li class="meta-type">{{ raceType }}</li>
        <li>{{ location }}</li>
        <li>{{ date }}</li>
        <li class="meta-weeks">
          {{ $t("next_race_in_weeks", { weeks: recommendation.weeks_out }) }}
        </li>
      </ul>

      <div v-if="recommendation.promo_code" class="countdown">
        <span class="countdown-label">
          {{ $t("next_race_promo", { percent: recommendation.promo_percent }) }}
          <b class="promo-code">{{ recommendation.promo_code }}</b>
        </span>
        <span v-if="timeLeft" class="countdown-units" role="timer" aria-live="off">
          <span v-for="u in units" :key="u.key" class="unit">
            <b>{{ pad(timeLeft[u.key]) }}</b>
            <small>{{ u.label }}</small>
          </span>
        </span>
      </div>

      <div class="card-actions">
        <mem-button class="card-button" btn-type="mem-primary" @click="register">
          {{ $t("next_race_cta") }}
        </mem-button>
        <router-link
          v-if="showRoadLink"
          class="road-link"
          :to="{ name: 'nextRacePage' }"
        >
          {{ $t("next_race_teaser_title") }} &rarr;
        </router-link>
      </div>
    </div>
  </section>
</template>
<script>
import moment from "moment";
import posthog from "posthog-js";
import { capture } from "@/plugins/posthog";
import { TS_CHECKOUT_COUNTRIES } from "@/common/const";
import { nextRaceCopy, countdown, raceLocation, offerEndsAt, saveRec } from "@/common/nextRace.mjs";

export default {
  props: {
    resultId: [String, Number],
    recommendation: { type: Object, required: true },
    showRoadLink: { type: Boolean, default: false },
  },
  components: {
    "mem-button": () => import("@/components/base/BaseButton.vue"),
  },
  data: () => ({ timeLeft: null }),
  computed: {
    event() {
      return this.recommendation.event;
    },
    image() {
      return this.event.image_large || this.event.image_small;
    },
    date() {
      return moment(this.event.start_date, "YYYY-MM-DD").format("MMM DD, YYYY");
    },
    location() {
      return raceLocation(this.event);
    },
    raceType() {
      return nextRaceCopy(this.recommendation).params.race_type;
    },
    units() {
      return [
        { key: "days", label: this.$t("d") },
        { key: "hours", label: this.$t("h") },
        { key: "minutes", label: this.$t("m") },
        { key: "seconds", label: this.$t("s") },
      ];
    },
    trackingProps() {
      let { race_type, reason_code, distance_km, weeks_out, discount_ends_at } =
        this.recommendation;
      return {
        result_id: this.resultId,
        recommended_event_id: this.event.id,
        race_type,
        reason_code,
        distance_km,
        weeks_out,
        has_discount: !!discount_ends_at,
        promo_code: this.recommendation.promo_code,
      };
    },
  },
  methods: {
    pad: (n) => String(n).padStart(2, "0"),
    // ponytail: first-seen lives in localStorage, so a new device or cleared storage restarts the window.
    // The code itself is not time-limited in TicketSocket. Upgrade: store first-seen server-side per athlete.
    offerDeadline() {
      let { discount_ends_at, promo_code } = this.recommendation;
      if (!promo_code || !discount_ends_at) return null;
      let key = `next_race_offer_seen_${this.event.id}`;
      let seen = localStorage.getItem(key);
      if (!seen) localStorage.setItem(key, (seen = new Date().toISOString()));
      return offerEndsAt(seen, discount_ends_at);
    },
    tick() {
      this.timeLeft = countdown(this.deadline);
      if (!this.timeLeft) clearInterval(this.timer);
    },
    register() {
      let id = this.recommendation.ticket_type_id;
      let region = this.event.ts_org_code.toLowerCase();
      let rec = { rec_source: "next_race_card", rec_id: `${this.event.id}` };
      let query = {
        goToCheckout: "1",
        [`cartTasks[ticketTypeId_${id}][typeId]`]: `${id}`,
        [`cartTasks[ticketTypeId_${id}][eventId]`]: `${this.event.ts_id}`,
        [`cartTasks[ticketTypeId_${id}][quantity]`]: "1",
        ...rec,
      };
      if (this.recommendation.promo_code)
        query.promocode = this.recommendation.promo_code;

      capture("next_race_card_clicked", this.trackingProps);
      posthog.register_for_session?.(rec);
      saveRec(localStorage, rec);

      if (TS_CHECKOUT_COUNTRIES.includes(region))
        return this.$router.push({
          name: "checkout",
          params: { region },
          query,
        });

      window.location.href = `https://tickets-${region}.spartan.com/api/shoppingCart/add/?${new URLSearchParams(
        query
      )}`;
    },
  },
  mounted() {
    this.deadline = this.offerDeadline();
    this.tick();
    if (this.timeLeft) this.timer = setInterval(this.tick, 1000);
    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      capture("next_race_card_viewed", this.trackingProps);
      this.observer.disconnect();
    });
    this.observer.observe(this.$el);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.observer.disconnect();
  },
};
</script>
<style lang="scss" scoped>
.next-race-card {
  position: relative;
  overflow: hidden;
  min-height: 300px;
  display: flex;
  align-items: center;
  color: #ffffff;
  background: #cf1019;
  .race-image {
    position: absolute;
    top: 0;
    right: 0;
    width: 62%;
    height: 100%;
    object-fit: cover;
  }
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      90deg,
      #cf1019 0%,
      #cf1019 38%,
      rgba(207, 16, 25, 0.75) 58%,
      rgba(0, 0, 0, 0.1) 100%
    );
  }
  .card-body {
    position: relative;
    z-index: 2;
    max-width: 560px;
    padding: 32px;
  }
  .card-label {
    font-family: Druk;
    font-size: 22px;
    font-weight: 900;
    line-height: 24px;
    text-transform: uppercase;
  }
  .card-sub {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.85;
  }
  .race-name {
    margin: 18px 0 10px;
    font-family: Druk;
    font-size: 48px;
    font-weight: 900;
    line-height: 48px;
    text-transform: uppercase;
  }
  .race-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    margin: 0 0 18px;
    padding: 0;
    list-style: none;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    li:not(:first-child):not(.meta-weeks)::before {
      content: "\2022";
      margin-right: 14px;
      opacity: 0.6;
    }
    .meta-type {
      padding: 2px 10px;
      background: #000000;
    }
    .meta-weeks {
      padding: 2px 10px;
      background: #ffffff;
      color: #cf1019;
    }
  }
  .countdown {
    display: inline-flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 22px;
    padding: 10px 14px;
    background: rgba(0, 0, 0, 0.35);
  }
  .countdown-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
  .promo-code {
    margin-left: 6px;
    padding: 1px 8px;
    border: 1px dashed #ffffff;
    letter-spacing: 0.1em;
  }
  .countdown-units {
    display: flex;
    gap: 12px;
  }
  .unit {
    display: flex;
    align-items: baseline;
    gap: 2px;
    b {
      font-family: Druk;
      font-size: 28px;
      font-variant-numeric: tabular-nums;
    }
    small {
      font-size: 11px;
      text-transform: uppercase;
    }
  }
  .card-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 20px;
  }
  .card-button {
    height: 44px;
    padding: 0 34px;
    border: 2px solid #ffffff;
    border-radius: 22px;
    background: #ffffff !important;
    color: #cf1019;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.08em;
    &:hover,
    &:focus-visible {
      background: transparent !important;
      color: #ffffff;
    }
  }
  .road-link {
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    text-decoration: underline;
    text-underline-offset: 4px;
  }
  @media screen and (max-width: $mobile) {
    flex-direction: column;
    align-items: stretch;
    .race-image {
      position: relative;
      width: 100%;
      height: 180px;
    }
    &::before {
      background: linear-gradient(180deg, transparent 0, transparent 120px, #cf1019 180px);
    }
    .card-body {
      padding: 20px;
    }
    .race-name {
      font-size: 34px;
      line-height: 36px;
    }
  }
}
</style>
